import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
  HStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Code,
  VStack,
} from '@chakra-ui/react';
import { FiEye, FiRefreshCw } from 'react-icons/fi';

const API_BASE = '/api';

function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchWebhooks = () => {
    setLoading(true);
    fetch(`${API_BASE}/webhooks/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setWebhooks(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const viewWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    onOpen();
  };

  const formatEventData = (data) => {
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      return data;
    }
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Webhook Events</Heading>
        <Button leftIcon={<FiRefreshCw />} onClick={fetchWebhooks}>
          Refresh
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Event Type</Th>
              <Th>Received At</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {webhooks.map(webhook => (
              <Tr key={webhook.id}>
                <Td fontWeight="medium">{webhook.id}</Td>
                <Td>
                  <Badge colorScheme="teal">{webhook.event}</Badge>
                </Td>
                <Td>{new Date(webhook.created_at).toLocaleString()}</Td>
                <Td>
                  <Button
                    leftIcon={<FiEye />}
                    size="sm"
                    variant="ghost"
                    onClick={() => viewWebhook(webhook)}
                  >
                    View
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {webhooks.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No webhook events found</Text>
        )}
      </Box>

      {/* View Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>Webhook Event Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedWebhook && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={1}>Event Type</Text>
                  <Badge colorScheme="teal" fontSize="md">{selectedWebhook.event}</Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={1}>Received At</Text>
                  <Text>{new Date(selectedWebhook.created_at).toLocaleString()}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Event Data</Text>
                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    overflowX="auto"
                    maxH="400px"
                    overflowY="auto"
                  >
                    <Code display="block" whiteSpace="pre" fontSize="sm">
                      {formatEventData(selectedWebhook.event_data)}
                    </Code>
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Webhooks;
